// Sidebar menu as pure data (PLAN.md §5.1 item 2, owner A). AppSidebar renders whatever buildNav returns, so
// the agency menu, the Hotel-Edition menu (Mike's sketch 2026-10-08) and the neutral view while the
// capability loads are all decided here and unit-tested without React.
import type { Edition, EditionModule } from "@shared/edition";
import { isNavItemHidden } from "./onboardingUiFlags";

/** Icon keys; AppSidebar maps them to lucide icons (keeps this file free of React). */
export type NavIcon =
  | "home" | "checkin" | "photos" | "sparkles" | "news" | "napkin" | "link" | "columns" | "palette"
  | "calendar" | "calendarClock" | "youtube" | "instagram" | "user" | "building" | "share" | "briefcase"
  | "crosshair" | "users" | "topics" | "image" | "clapperboard" | "linkedin" | "brand";

export interface NavItem {
  title: string;
  url: string;
  icon: NavIcon;
  /** Shows the "x/6" check-in progress badge. */
  checkinBadge?: boolean;
  /** Locked extra module: no link, a click only shows LOCKED_MODULE_HINT. */
  locked?: boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
  /** Rendered as a folded group (closed by default) with `note` under the label. */
  collapsible?: boolean;
  note?: string;
}

export type NavModel =
  | { kind: "loading" }
  | { kind: "menu"; header: NavHeader; sections: NavSection[] };

export interface NavHeader {
  title: string;
  subtitle: string;
  /** Subtitle shown bold in the accent colour (the hotel's "Hotel-Edition", Mike 2026-10-09 Befund 17). */
  emphasis?: boolean;
}

export interface NavInput {
  /** null while the capability loads; "error" when /api/companies/me failed (e.g. no company yet). */
  edition: Edition | null | "error";
  isDfy: boolean;
  isAdmin: boolean;
  srMapped: boolean;
  checkinOpen: boolean;
}

export const LOCKED_MODULE_HINT = "Auf Anfrage freischaltbar. Sprechen Sie Ihr Team an.";

/** Extra modules a hotel sees folded and locked (Mike A3). Their routes send hotels to "/" (App.tsx). */
export const HOTEL_LOCKED_ROUTES: readonly string[] = [
  "/evergreen-content", "/industry-news", "/napkin-wisdom", "/repurposing", "/brand-visuals",
];

export function isHotelLockedRoute(path: string): boolean {
  return HOTEL_LOCKED_ROUTES.includes(path);
}

const AGENCY_HEADER = { title: "Ihr A-Team", subtitle: "Company Portal" };
const HOTEL_HEADER: NavHeader = { title: "Social Media A-Team", subtitle: "Hotel-Edition", emphasis: true };

interface Rules {
  dfyOnly?: boolean;
  signalRoomOnly?: boolean;
  adminOnly?: boolean;
  checkinOnly?: boolean;
}
type RuledItem = NavItem & Rules;

function visible(items: RuledItem[], input: NavInput): NavItem[] {
  return items
    .filter((i) => !isNavItemHidden(i.url))
    .filter((i) => !i.dfyOnly || input.isDfy)
    .filter((i) => !i.signalRoomOnly || input.srMapped)
    .filter((i) => !i.adminOnly || input.isAdmin)
    .filter((i) => !i.checkinOnly || input.checkinOpen)
    .map(({ dfyOnly, signalRoomOnly, adminOnly, checkinOnly, ...item }) => item);
}

const NAVIGATION: RuledItem[] = [
  { title: "Cockpit", url: "/", icon: "home" },
  // Agencies: shown until the check-in is hidden (companies.checklist_dismissed_at), with "x/6". Hotels always
  // keep it (Mike 2026-10-09, Befund 1): "ausblenden" only stops it being the start page, the page promises
  // "Sie können jederzeit hierher zurückkommen".
  { title: "Check-In", url: "/check-in", icon: "checkin", checkinOnly: true, checkinBadge: true },
];

const ACCOUNT: RuledItem[] = [
  { title: "Team", url: "/team", icon: "users" }, // hidden for everyone (onboardingUiFlags), route stays
  { title: "Firmenprofil", url: "/profil", icon: "building" },
  { title: "Passwort ändern", url: "/passwort", icon: "user" },
];

/** Today's agency menu. Cockpit-Tour and Willkommensvideo are gone for everyone (Mike 2026-10-08, PLAN §1). */
function agencySections(input: NavInput): NavSection[] {
  return [
    { label: "Navigation", items: visible(NAVIGATION, input) },
    {
      label: "Content Management",
      items: visible([
        { title: "Foto-Bibliothek", url: "/foto-bibliothek", icon: "photos" },
        // No n8n supplier behind it (system map 04.10.): route stays, customers get no menu item.
        { title: "Evergreen Content", url: "/evergreen-content", icon: "sparkles", adminOnly: true },
        { title: "Branchen-News", url: "/industry-news", icon: "news" },
        { title: "Napkin Wisdom", url: "/napkin-wisdom", icon: "napkin", adminOnly: true },
        { title: "Inhalte wiederverwenden", url: "/repurposing", icon: "link" },
        { title: "Beiträge nach Themen", url: "/pillar-posts", icon: "columns" },
        { title: "Brand Visuals", url: "/brand-visuals", icon: "palette" },
        { title: "Veröffentlichungsplan", url: "/veroeffentlichungsplan", icon: "calendar" },
        { title: "Wochenplan", url: "/wochenplan", icon: "calendarClock", dfyOnly: true },
        { title: "Repurposing-Quellen", url: "/repurposing-quellen", icon: "youtube", dfyOnly: true },
        { title: "Instagram-Säulen", url: "/instagram-saeulen", icon: "instagram", signalRoomOnly: true },
      ], input),
    },
    {
      label: "Social Media Setup",
      items: visible([
        { title: "Marketing Setup", url: "/marketing-setup", icon: "sparkles" },
        { title: "A-Team Briefing", url: "/a-team-briefing", icon: "briefcase" },
        { title: "Marketingstrategie", url: "/marketingstrategie", icon: "crosshair" },
        { title: "Plattform verbinden", url: "/platform-connections", icon: "share" },
      ], input),
    },
    { label: "Account Einstellungen", items: visible(ACCOUNT, input) },
  ];
}

/** Hotel-Edition menu after Mike's sketch. Module entries (¹ in PLAN §5.1) appear only when the server lists
 *  the module; until then the old entries ("Instagram-Säulen", "Beiträge nach Themen") keep their place. */
function hotelSections(input: NavInput, modules: readonly EditionModule[]): NavSection[] {
  const has = (m: EditionModule) => modules.includes(m);
  return [
    { label: "Navigation", items: visible(NAVIGATION, { ...input, checkinOpen: true }) },
    {
      label: "Content Management",
      items: visible([
        { title: "Post-Bibliothek", url: "/foto-bibliothek", icon: "photos" }, // Mike 2026-10-10; route unchanged
        has("themen")
          ? { title: "Themenkatalog", url: "/themenkatalog", icon: "topics" }
          : { title: "Instagram-Säulen", url: "/instagram-saeulen", icon: "instagram", signalRoomOnly: true },
        ...(has("fotos") ? [{ title: "Instagram-Fotos", url: "/instagram-fotos", icon: "image" as const }] : []),
        ...(has("reels") ? [{ title: "Instagram-Reels", url: "/instagram-reels", icon: "clapperboard" as const }] : []),
        has("linkedin")
          ? { title: "LinkedIn-Beiträge", url: "/linkedin-beitraege", icon: "linkedin" }
          : { title: "Beiträge nach Themen", url: "/pillar-posts", icon: "columns" },
        { title: "Wochenplan", url: "/wochenplan", icon: "calendarClock", dfyOnly: true },
        { title: "Veröffentlichungsplan", url: "/veroeffentlichungsplan", icon: "calendar" },
      ], input),
    },
    {
      label: "Zusatzmodule",
      collapsible: true,
      note: LOCKED_MODULE_HINT,
      items: [
        { title: "Evergreen Content", url: "/evergreen-content", icon: "sparkles", locked: true },
        { title: "Branchen-News", url: "/industry-news", icon: "news", locked: true },
        { title: "Napkin Wisdom", url: "/napkin-wisdom", icon: "napkin", locked: true },
        { title: "Inhalte wiederverwenden", url: "/repurposing", icon: "link", locked: true },
        { title: "Brand Visuals", url: "/brand-visuals", icon: "palette", locked: true },
      ],
    },
    {
      label: "Social Media Setup",
      items: visible([
        ...(has("marke") ? [{ title: "Marke", url: "/marke", icon: "brand" as const }] : []),
        { title: "Marketing Setup", url: "/marketing-setup", icon: "sparkles" },
        { title: "A-Team Briefing", url: "/a-team-briefing", icon: "briefcase" },
        { title: "LinkedIn-Strategie", url: "/marketingstrategie", icon: "crosshair" },
        { title: "Repurposing-Quellen", url: "/repurposing-quellen", icon: "youtube", dfyOnly: true },
        { title: "Plattform verbinden", url: "/platform-connections", icon: "share" },
      ], input),
    },
    { label: "Konto", items: visible(ACCOUNT, input) },
  ];
}

/** Neutral menu when the company cannot be read (PLAN §2, R1-14): neither agency nor hotel entries, only
 *  what every signed-in user can open. Admins keep their own section (rendered by AppSidebar). */
function neutralSections(input: NavInput): NavSection[] {
  return [
    { label: "Navigation", items: visible([NAVIGATION[0]], input) },
    { label: "Konto", items: visible(ACCOUNT, input) },
  ];
}

export function buildNav(input: NavInput): NavModel {
  const { edition } = input;
  if (edition === null) return { kind: "loading" };
  if (edition === "error") return { kind: "menu", header: { title: "Ihr A-Team", subtitle: "" }, sections: neutralSections(input) };
  if (edition.kind === "hotel") return { kind: "menu", header: HOTEL_HEADER, sections: hotelSections(input, edition.modules) };
  return { kind: "menu", header: AGENCY_HEADER, sections: agencySections(input) };
}
